import { toast } from '@Shelly/shelly-design-system';
import { parseSchedules, prepareToSave } from 'src/helpers/actions/schedules/core/schedule_helpers';
import { combineLists } from 'src/components/DevicesSettings/Schedules/SchedulesHelpers/schedules';
import { digitFormat } from 'src/helpers/date_helper';
import { celsius_to_fahrenheit, is_gen_2 } from 'src/helpers/interface_helpers';
import { deepCopy } from 'src/helpers/utilities';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import type { IChartTempSchedule } from 'src/types/TemperatureSchemeType';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { macFromId } from 'src/helpers/interface_helpers';

//this will extract id from sensor or actuator
export const parseID = (string) => {
	let channel = '',
		parsed = '';
	if (!string) return parsed;

	if (string?.includes('bthome:')) {
		parsed = 'XB' + parseInt(string.split('/')[2], 16);
		return parsed;
	}
	// get the device ID with the channel included
	if (string[string.length - 1] !== '0') channel = '_' + string[string.length - 1];
	parsed = string?.split('/')?.[2]?.split('-')?.[1]?.toLowerCase();
	if (channel) parsed = parsed + channel;

	return parsed;
};

// This function creates a URI for a sensor or actuator
export const generateURI = (string, id) => {
	// Explanation: https://gitlab.allterco.net/Shelly/android/shelly-stargate/-/wikis/Shelly-URI-Schema#bthome-uri-schema

	// Split the input string by '/'
	let dataChunked = string.split('/');

	// Check if the device is a GBLE type
	const isGBLE = get(devices)[id]?.gen === 'GBLE';

	// Check if the device is a Gen2 type
	const isGen2 = is_gen_2(get(devices)[id]);

	// Determine the device name from settings or device information
	const deviceName =
		devices_settings.getByRealId(id)?.['DeviceInfo']?.['id'] ||
		devices_settings.getByRealId(id)?.['device']?.['hostname'] ||
		get(devices)[id]?.['mac'] ||
		macFromId(id); // Device MAC is for GBLE devices. macFromId(id) is for GBLE devices which for some reason doesn't have a MAC

	// Determine the prefix based on device type
	const prefix = isGen2 ? 'shelly:' : isGBLE ? 'bthome:' : `shelly+${get(devices)[id].gen}:`; // shelly: for Gen2, bthome: for GBLE, shelly+gen for legacy

	// Determine the device channel
	const deviceStructure = (isGBLE ? 'temperature:' : 'switch:') + get(devices)[id]?.channel.toString();

	// Replace specific elements in the dataChunked array with prefix, device name, and device channel
	dataChunked.splice(0, 1, prefix);
	dataChunked.splice(2, 1, deviceName);
	dataChunked.splice(4, 1, deviceStructure);

	// Join the modified dataChunked array with '/' and return the final URI
	return dataChunked.join('/');
};

export const translateThermostatData = (data, target: number, units: 'C' | 'F'): IChartTempSchedule => {
	const intervals = [];
	data.schedules.map((schedule) => {
		if (schedule.enable) {
			intervals.push({
				base: schedule.target_C,
				days: schedule.days.map((i: number) => (i = i == 0 ? 6 : i - 1)),
				time: [schedule.time],
			});
		}
	});
	return { base: target, units, intervals };
};

export const loadScheduleProfiles = async (device, activeProfile = 0, unit = 'C') => {
	let profiles = {},
		targetTemperature;

	const getProfiles: SettingRequest = await gen2Request(device, 'Thermostat.Schedule.ListProfiles', {
		id: 0,
	});

	if (!getProfiles.isok) return toast.open({ type: 'danger', title: get(format)('error_global') });
	const profilesRes =
		//@ts-ignore
		getProfiles?.data?.profiles || getProfiles?.profiles || [];
	profilesRes.forEach((profile) => (profiles[profile.id] = { rules: [], ...profile }));

	const respondedRules: SettingRequest = await gen2Request(device, 'Thermostat.Schedule.ListRules', {
		id: 0,
		profile_id: activeProfile,
	});

	if (respondedRules.isok) {
		const _device = deepCopy(device),
			rules = [];
		_device.mode = 'thermostat'; //overriding because wall display is in mode relay, because of that label is as brightness

		//@ts-ignore
		const rulesRes = respondedRules?.data?.rules || respondedRules?.rules || [];
		rulesRes.forEach((rule) => {
			rule.timespec = rule.timespec.replace(/^\*/, '0');
			rule.unit = unit;
			rule.temperature = `${unit === 'F' ? celsius_to_fahrenheit(rule.target_C) : rule.target_C} °${unit}`;
			rule = parseSchedules(rule);
			rule.enabled = rule.enable;
			rule.time = `${digitFormat(rule.hour)}:${digitFormat(rule.min)}`;
			rule.labels = combineLists(
				{
					temperature: rule.temperature,
					days: rule.days.map((i: number) => (i = i == 0 ? 6 : i - 1)),
					mode: 'Time',
					selection: undefined,
					sun: undefined,
					time: rule.time,
				},
				false,
				_device,
				rule.enable,
			);
			if (!Object.keys(profiles).includes(`${activeProfile}`)) {
				//Pako - this is prevention from failure because device's status gives us old profile id but inside the rules it's correct. ToDo need to remove after FW fixes
				activeProfile = 0;
				rule.profile_id = activeProfile;
			}
			rules.push(rule);
		});
		if (Object.keys(profiles).length > 0) {
			profiles[activeProfile].rules = rules;
			targetTemperature = rules?.[0]?.target_C;
		}
	} else {
		toast.open({ type: 'danger', title: get(format)('error_global') });
	}

	return profiles;
};

export const prepareScheduleToSend = (data) => {
	const prepareData = prepareToSave(data);
	return {
		config: {
			id: 0, //this will became dynamic someday
			index: data.index,
			profile_id: data.profile_id,
			target_C: data.target_C,
			rule_id: data.rule_id,
			timespec: prepareData.timespec,
			enable: data.enable,
		},
	};
};

// Function to handle hysteresis error

export function handleHysteresisError(hysteresis, minValueHysteresis, maxValueHysteresis) {
	let handleHysteresisError = '';
	if (
		typeof hysteresis === 'undefined' ||
		String(hysteresis) === '' ||
		isNaN(+hysteresis) ||
		hysteresis < minValueHysteresis ||
		hysteresis > maxValueHysteresis
	) {
		handleHysteresisError = get(format)('remain_awake_info_toast', {
			values: { min: minValueHysteresis, max: maxValueHysteresis },
			default: `Values must be between ${minValueHysteresis} and ${maxValueHysteresis}.`,
		});
	} else {
		handleHysteresisError = '';
	}
	return handleHysteresisError;
}
